import get from 'lodash.get';

import type { Field } from '../../fields/types/main';
import type { DependenciesFormValues } from '../../types/FormValues';
import type { SectionDependsOnDescriptor } from '../../sections/types';
import type { FieldDependsOnDescriptor } from '../../fields/types/common';

import type { FormattedErrors } from './formatZodErrors';

const isDependencyValueWithoutErrors = (
  sectionKey: string,
  dependency: FieldDependsOnDescriptor | SectionDependsOnDescriptor,
  errors: FormattedErrors | null
) => {
  if (dependency.ignoreError) {
    return true;
  }

  if ('isOptional' in dependency && dependency.isOptional) {
    return true;
  }

  if ('$or' in dependency) {
    return dependency.$or.some((orDependency) =>
      isDependencyValueWithoutErrors(sectionKey, orDependency, errors)
    );
  }

  if ('$and' in dependency) {
    return dependency.$and.every((andDependency) =>
      isDependencyValueWithoutErrors(sectionKey, andDependency, errors)
    );
  }
  if ('sectionKey' in dependency && 'fieldKey' in dependency) {
    if (errors[dependency.sectionKey] === undefined) {
      return true;
    }
    if (Array.isArray(errors[dependency.sectionKey])) {
      return false;
    }
    return get(errors, [dependency.sectionKey, ...dependency.fieldKey.split('.')]) === undefined;
  }

  if ('sectionKey' in dependency) {
    return errors[dependency.sectionKey] === undefined;
  }

  if ('fieldKey' in dependency) {
    if (errors[sectionKey] === undefined) {
      return true;
    }
    if (Array.isArray(errors[sectionKey])) {
      return false;
    }
    return get(errors, [sectionKey, ...dependency.fieldKey.split('.')]) === undefined;
  }
  if ('all' in dependency) {
    return errors === null || Object.keys(errors).filter((key) => key !== sectionKey).length === 0;
  }

  return false;
};

const isDependencyValuePresent = (
  dependency: FieldDependsOnDescriptor | SectionDependsOnDescriptor,
  values: DependenciesFormValues
) => {
  if ('$or' in dependency) {
    return dependency.$or.some((orDependency) => isDependencyValuePresent(orDependency, values));
  }
  if ('$and' in dependency) {
    return dependency.$and.every((andDependency) =>
      isDependencyValuePresent(andDependency, values)
    );
  }

  if ('isOptional' in dependency && dependency.isOptional) {
    return true;
  }

  const value = (() => {
    if ('sectionKey' in dependency && 'fieldKey' in dependency) {
      return get(values, [dependency.sectionKey, ...dependency.fieldKey.split('.')]);
    }
    if ('fieldKey' in dependency) {
      return get(values, dependency.fieldKey.split('.'));
    }
    if ('sectionKey' in dependency) {
      return values[dependency.sectionKey];
    }
    return null;
  })();

  return typeof value !== 'undefined';
};

const isDependencySatisfiesCondition = (
  dependency: Field['dependsOn'][0] | SectionDependsOnDescriptor,
  values: DependenciesFormValues = {}
) => {
  if ('$or' in dependency) {
    return dependency.$or.some((orDependency) =>
      isDependencySatisfiesCondition(orDependency, values)
    );
  }
  if ('$and' in dependency) {
    return dependency.$and.every((andDependency) =>
      isDependencySatisfiesCondition(andDependency, values)
    );
  }
  if (!('conditions' in dependency)) {
    return true;
  }

  const conditions = dependency.conditions;
  const value =
    'sectionKey' in dependency
      ? get(values, [dependency.sectionKey, ...dependency.fieldKey.split('.')])
      : get(values, dependency.fieldKey.split('.'));

  const conditionsResults = Object.entries(conditions).map(([k, v]) => {
    switch (k) {
      case '$eq':
        return value === v;
      case '$neq':
        return value !== v;
      case '$lt':
        return value < v;
      case '$lte':
        return value <= v;
      case '$gt':
        return value > v;
      case '$gte':
        return value >= v;
      case '$in':
        if (Array.isArray(value)) {
          return value.every((item) => (v as (string | number)[]).includes(item));
        }
        return (v as (string | number)[]).includes(value);
      case '$nin':
        return !(v as (string | number)[]).includes(value);
      default:
        return true;
    }
  });

  return conditionsResults.length ? conditionsResults.every((result) => result) : true;
};

export const getIsDependenciesSatisfied = (
  sectionKey: string,
  // eslint-disable-next-line @typescript-eslint/default-param-last
  dependsOn: FieldDependsOnDescriptor[] | SectionDependsOnDescriptor[] = [],
  errors: FormattedErrors | null,
  values: DependenciesFormValues = {}
) =>
  dependsOn.every((dependency) => {
    const isValueWithoutErrors = errors
      ? isDependencyValueWithoutErrors(sectionKey, dependency, errors)
      : true;
    if (!isValueWithoutErrors) {
      return false;
    }
    const isValueValid = isDependencyValuePresent(dependency, values);
    if (!isValueValid) {
      return false;
    }

    return isDependencySatisfiesCondition(dependency, values);
  });
